import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Users, Plus, Search, Loader2, BookOpen, Lock, Globe } from "lucide-react";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useRequireAuth } from "@/hooks/useRequireAuth";
import AuthPromptDialog from "@/components/AuthPromptDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

interface Circle {
  id: string;
  name: string;
  description: string | null;
  skill_id: string | null;
  creator_id: string;
  max_members: number;
  is_public: boolean;
  member_count?: number;
}

const LearningCircles = () => {
  const navigate = useNavigate();
  const [circles, setCircles] = useState<Circle[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newCircle, setNewCircle] = useState({
    name: "",
    description: "",
    max_members: 10,
    is_public: true
  });
  const { user, requireAuth, showAuthPrompt, closeAuthPrompt, promptOptions } = useRequireAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchCircles();
  }, []);

  const fetchCircles = async () => {
    try {
      const { data, error } = await supabase
        .from('learning_circles')
        .select('*')
        .eq('is_public', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch member counts for each circle
      const circlesWithCounts = await Promise.all(
        (data || []).map(async (circle) => {
          const { count } = await supabase
            .from('learning_circle_members')
            .select('*', { count: 'exact', head: true })
            .eq('circle_id', circle.id);

          return { ...circle, member_count: count || 0 };
        })
      );

      setCircles(circlesWithCounts);
    } catch (error: any) {
      console.error('Error fetching circles:', error);
    } finally {
      setLoading(false);
    }
  };

  const createCircle = async () => {
    requireAuth(async () => {
      if (!newCircle.name.trim()) {
        toast({
          title: "Name required",
          description: "Please enter a name for your learning circle",
          variant: "destructive"
        });
        return;
      }

      try {
        const { data, error } = await supabase
          .from('learning_circles')
          .insert({
            ...newCircle,
            creator_id: user!.id
          })
          .select()
          .single();

        if (error) throw error;

        // Auto-join as creator
        await supabase
          .from('learning_circle_members')
          .insert({
            circle_id: data.id,
            user_id: user!.id,
            role: 'creator'
          });

        toast({
          title: "Circle created!",
          description: "Your learning circle is ready 🎉"
        });

        setCreateOpen(false);
        setNewCircle({
          name: "",
          description: "",
          max_members: 10,
          is_public: true
        });
        fetchCircles();
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      }
    }, { title: "Join to Create", description: "Sign up to create your own learning circles!" });
  };

  const joinCircle = async (circleId: string) => {
    requireAuth(async () => {
      try {
        const { error } = await supabase
          .from('learning_circle_members')
          .insert({
            circle_id: circleId,
            user_id: user!.id
          });

        if (error) throw error;

        toast({
          title: "Joined successfully!",
          description: "Welcome to the learning circle 🎓"
        });

        fetchCircles();
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      }
    }, { title: "Join to Learn", description: "Sign up to join learning circles and connect with learners!" });
  };

  const filteredCircles = circles.filter(circle =>
    circle.name.toLowerCase().includes(search.toLowerCase()) ||
    circle.description?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center pb-24">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <Navigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      <AuthPromptDialog 
        open={showAuthPrompt} 
        onOpenChange={closeAuthPrompt}
        title={promptOptions.title}
        description={promptOptions.description}
      />
      <header className="gradient-primary text-white p-6 pb-12 rounded-b-3xl shadow-strong">
        <div className="container max-w-md mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8" />
              <div>
                <h1 className="text-2xl font-bold">Learning Circles</h1>
                <p className="text-sm text-white/80">Learn together, grow faster</p>
              </div>
            </div>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button variant="secondary" size="icon">
                  <Plus className="h-5 w-5" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Learning Circle</DialogTitle>
                  <DialogDescription>
                    Start a group to learn and share skills together
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Circle Name *</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Web Dev Beginners"
                      value={newCircle.name}
                      onChange={(e) => setNewCircle({ ...newCircle, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="What will this circle focus on?"
                      value={newCircle.description}
                      onChange={(e) => setNewCircle({ ...newCircle, description: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="max_members">Max Members</Label>
                    <Input
                      id="max_members"
                      type="number"
                      min="2"
                      max="50"
                      value={newCircle.max_members}
                      onChange={(e) => setNewCircle({ ...newCircle, max_members: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="public">Public Circle</Label>
                    <Switch
                      id="public"
                      checked={newCircle.is_public}
                      onCheckedChange={(checked) => setNewCircle({ ...newCircle, is_public: checked })}
                    />
                  </div>
                  <Button onClick={createCircle} className="w-full gradient-primary text-white">
                    Create Circle
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
            <Input
              placeholder="Search circles..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white/20 border-white/30 text-white placeholder:text-white/60"
            />
          </div>
        </div>
      </header>

      <main className="container max-w-md mx-auto px-4 -mt-6 space-y-4">
        {filteredCircles.length === 0 ? (
          <Card className="p-8 text-center shadow-soft">
            <BookOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-bold text-lg mb-2">No Circles Found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {search ? "Try a different search term" : "Be the first to create a learning circle!"}
            </p>
            <Button onClick={() => setCreateOpen(true)} variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              Create Circle
            </Button>
          </Card>
        ) : (
          filteredCircles.map((circle) => (
            <Card key={circle.id} className="p-5 shadow-soft hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-lg">{circle.name}</h3>
                    {circle.is_public ? (
                      <Globe className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <Lock className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  {circle.description && (
                    <p className="text-sm text-muted-foreground mb-2">
                      {circle.description}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>{circle.member_count || 0}/{circle.max_members}</span>
                </div>
                <Button 
                  size="sm"
                  onClick={() => navigate(`/circles/${circle.id}`)}
                  className="gradient-primary text-white"
                >
                  View Circle
                </Button>
              </div>
            </Card>
          ))
        )}
      </main>

      <Navigation />
    </div>
  );
};

export default LearningCircles;
